'use strict'

const Action = require('../action')
const BasePolicy = require('../common/policy')
const Q = require('q')
const Util = require('../common/util')

const defaultVersion = '2017-02-15'

const allActions = () => {
    return Object.keys(Action)
        .reduce((all, serviceName) => {
            const service = Action[serviceName]
            const actions = Object.keys(service)
                .map(activityName => {
                    return service[activityName]
                })
            all.push.apply(all, actions)
            return all
        }, [])
}

class Policy extends BasePolicy {

    constructor(document, fetchProperties) {
        super(document, defaultVersion)
        this.fetchProperties = fetchProperties
        this.cachedResourceKeyRegex = {}
        this.cachedGetServiceByResource = {}
    }

    innerFactory(document) {
        return new Policy(document, this.fetchProperties)
    }

    getProperties(type) {
        if (!this.fetchProperties) {
            return Q.reject(new Error('Missing properties fetcher'))
        }
        if (undefined === type) {
            type = Util.Wildcard
        }
        const activityMap = this.document.Statement.reduce((map, statement) => {
            return statement.Action.reduce((m, action) => {
                m[action] = true
                return m
            }, map)
        }, {})
        const activities = Object.keys(activityMap).map(Util.parseAction)
        return this.fetchProperties(activities, type)
    }

    _getServiceByResource(resource) {
        let services = this.servicesByResource[resource]

        if (services) {
            return [services]
        }

        services = this.cachedGetServiceByResource[resource]

        if (services) {
            return services
        }

        const resourceKeys = Object.keys(this.servicesByResource).filter(key => {
            if (key.indexOf(Util.Wildcard) < 0) {
                return false
            }

            let regex = this.cachedResourceKeyRegex[key]

            if (!regex) {
                const regexStr = key === '*'
                    ? '.*'
                    : '^' + key.replace(/\*/g, '(\\w+|\\*)') + '$'
                regex = new RegExp(regexStr)
                this.cachedResourceKeyRegex[key] = regex
            }

            return regex.test(resource)
        })

        if (!resourceKeys.length) {
            services = []
        }

        services = resourceKeys.map((key) => {
            return this.servicesByResource[key]
        })

        this.cachedGetServiceByResource[resource] = services

        return services
    }

    allows(action, orn) {
        const parsedAction = Util.parseAction(action)
        return [Util.Wildcard, orn].some(resource => {
            const services = this._getServiceByResource(resource)
            return services.some(service => {
                if (service[Util.Wildcard]) {
                    return true
                }
                const activities = service[parsedAction.service]
                if (!activities) {
                    return false
                }
                if (activities[Util.Wildcard]) {
                    return true
                }
                if (parsedAction.activity && activities[parsedAction.activity]) {
                    return true
                }
                return false
            })
        })
    }

    /**
     * Unroll all wildcard actions and provide all actions as a list of
     * objects
     */
    enumerateActions() {
        const statements = Array.isArray(this.document.Statement)
            ? this.document.Statement
            : []
        return statements.reduce((results, statement) => {
            const resources = Util.asArray(statement.Resource)
            const actions = Array.isArray(statement.Action)
                ? statement.Action
                : []
            const expandedActions = actions.reduce((expandedSet, action) => {
                if (Util.Wildcard === action) {
                    allActions().forEach(a => {
                        expandedSet[a] = true
                    })
                    return expandedSet
                }
                const actionObj = Util.parseAction(action)
                if (Util.Wildcard === actionObj.activity) {
                    const service = Action[actionObj.service]
                    Object.keys(service).reduce((set, activity) => {
                        expandedSet[service[activity]] = true
                        return set
                    }, expandedSet)
                }
                else {
                    expandedSet[action] = true
                }
                return expandedSet
            }, {})
            return Object.keys(expandedActions).reduce((r, actionString) => {
                const action = Util.parseAction(actionString)
                const mappedToResources = resources.map(resource => {
                    return {
                        action,
                        effect: statement.Effect,
                        resource
                    }
                })
                r.push.apply(r, mappedToResources)
                return r
            }, results)
        }, [])
    }

}

module.exports = {
    create(document, fetchProperties) {
        return new Policy(document, fetchProperties)
    }
}



//////////////////
// WEBPACK FOOTER
// ./~/@gsm/authorizer/lib/access-policy/policy.js
// module id = 160
// module chunks = 51